import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { TrendingUp, TrendingDown, Warning, CheckCircle, AccountBalance, Assessment } from '@mui/icons-material';
import Grid from '@mui/material/GridLegacy';
import { Portfolio, Position } from '../types';
import { PortfolioService } from '../services/portfolioService';
import { PositionService } from '../services/positionService';
import { TradeManager, PortfolioStatus } from '../services/tradeManager';
import CreatePortfolioDialog from '../components/CreatePortfolioDialog';
import ExecutePendingPositionDialog from '../components/ExecutePendingPositionDialog';
import WelcomeDialog from '../components/WelcomeDialog';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';
import { useMobileDetect } from '../hooks/useMobileDetect';

const Dashboard: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [positions, setPositions] = useState<Record<string, Position[]>>({});
  const [pendingPositions, setPendingPositions] = useState<Record<string, Position[]>>({});
  const [closedPositions, setClosedPositions] = useState<Record<string, Position[]>>({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [executePendingDialog, setExecutePendingDialog] = useState<{
    open: boolean;
    position: Position | null;
  }>({ open: false, position: null });
  const navigate = useNavigate();
  const { isMobile } = useMobileDetect();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setError(null);
    try {
      const allPortfolios = await PortfolioService.getAllPortfolios();
      // Filter out inactive portfolios
      const activePortfolios = allPortfolios.filter(p => p.isActive !== false);
      setPortfolios(activePortfolios);
      
      // Load positions for each active portfolio
      const positionsMap: Record<string, Position[]> = {};
      const pendingMap: Record<string, Position[]> = {};
      const closedMap: Record<string, Position[]> = {};
      for (const portfolio of activePortfolios) {
        try {
          const [openPositions, pending, closed] = await Promise.all([
            PositionService.getOpenPositions(portfolio.id),
            PositionService.getPendingPositions(portfolio.id),
            PositionService.getClosedPositions(portfolio.id)
          ]);
          positionsMap[portfolio.id] = openPositions;
          pendingMap[portfolio.id] = pending;
          closedMap[portfolio.id] = closed;
        } catch (error) {
          console.error(`Error loading positions for portfolio ${portfolio.id}:`, error);
          positionsMap[portfolio.id] = [];
          pendingMap[portfolio.id] = [];
          closedMap[portfolio.id] = [];
        }
      }
      setPositions(positionsMap);
      setPendingPositions(pendingMap);
      setClosedPositions(closedMap);
    } catch (error: any) {
      console.error('Error loading dashboard:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePortfolioCreated = (portfolio: Portfolio) => {
    setCreateDialogOpen(false);
    loadDashboardData();
  };

  const calculatePortfolioMetrics = (
    portfolio: Portfolio, 
    portfolioPositions: Position[] = [],
    portfolioPendingPositions: Position[] = []
  ) => {
    // Only count open positions
    const openPositions = portfolioPositions.filter(p => p.status === 'open');
    
    // Current value of open positions
    const positionsValue = openPositions.reduce(
      (sum, p) => sum + p.currentPrice * p.quantity,
      0
    );
    
    // Cost basis of open positions (what we paid)
    const positionsCost = openPositions.reduce(
      (sum, p) => sum + p.entryPrice * p.quantity,
      0
    );
    
    // Pending positions allocation
    const pendingAllocations = portfolioPendingPositions.reduce(
      (sum, p) => sum + (p.limitPrice || p.entryPrice) * p.quantity,
      0
    );
    
    // Performance is based only on executed positions
    const positionsGainLoss = positionsValue - positionsCost;
    const positionsGainLossPercent = positionsCost > 0 ? (positionsGainLoss / positionsCost) * 100 : 0;
    
    // Available funds calculation (fund size - open positions cost + losses)
    // We only subtract losses, we don't add gains to available funds
    const realizedLosses = Math.min(0, portfolio.totalEarnings || 0);
    const availableFunds = portfolio.initialFundValue - positionsCost + realizedLosses;
    const allocatedPercent = (positionsCost / portfolio.initialFundValue) * 100;
    const totalFundUsedPercent = ((positionsCost + pendingAllocations) / portfolio.initialFundValue) * 100;

    return {
      totalValue: positionsValue, // Only executed positions value
      totalGainLoss: positionsGainLoss,
      totalGainLossPercent: positionsGainLossPercent,
      positionsValue,
      positionCount: openPositions.length,
      availableFunds,
      allocatedPercent,
      pendingAllocations,
      totalFundUsedPercent,
      pendingCount: portfolioPendingPositions.length,
    };
  };

  const calculateOverallMetrics = () => {
    const realMetrics = {
      totalValue: 0,
      totalInitial: 0,
      totalPositions: 0,
      totalInvested: 0,
      totalPositionsGainLoss: 0,
      totalEarnings: 0,
      portfolioCount: 0,
    };

    const virtualMetrics = {
      totalValue: 0,
      totalInitial: 0,
      totalPositions: 0,
      totalInvested: 0,
      totalPositionsGainLoss: 0,
      totalEarnings: 0,
      portfolioCount: 0,
    };

    portfolios.forEach((portfolio) => {
      const isReal = portfolio.type === 'real';
      const metrics = isReal ? realMetrics : virtualMetrics;
      
      const portfolioPositions = positions[portfolio.id] || [];
      const portfolioMetrics = calculatePortfolioMetrics(portfolio, portfolioPositions);
      
      metrics.totalValue += portfolioMetrics.totalValue;
      metrics.totalInitial += portfolio.initialFundValue;
      metrics.totalPositions += portfolioMetrics.positionCount;
      metrics.totalPositionsGainLoss += portfolioMetrics.totalGainLoss;
      metrics.totalEarnings += (portfolio.totalEarnings || 0);
      metrics.portfolioCount += 1;
      
      // Calculate total invested (cost of open positions)
      const openPositions = portfolioPositions.filter(p => p.status === 'open');
      const positionsCost = openPositions.reduce(
        (sum, p) => sum + p.entryPrice * p.quantity,
        0
      );
      metrics.totalInvested += positionsCost;
    });

    // Calculate derived metrics for each type
    const realTotalCash = realMetrics.totalInitial - realMetrics.totalInvested;
    const realTotalGainLossPercent = realMetrics.totalInitial > 0 
      ? (realMetrics.totalPositionsGainLoss / realMetrics.totalInitial) * 100 
      : 0;

    const virtualTotalCash = virtualMetrics.totalInitial - virtualMetrics.totalInvested;
    const virtualTotalGainLossPercent = virtualMetrics.totalInitial > 0 
      ? (virtualMetrics.totalPositionsGainLoss / virtualMetrics.totalInitial) * 100 
      : 0;

    // Combined metrics
    const combined = {
      totalValue: realMetrics.totalValue + virtualMetrics.totalValue,
      totalInitial: realMetrics.totalInitial + virtualMetrics.totalInitial,
      totalGainLoss: realMetrics.totalPositionsGainLoss + virtualMetrics.totalPositionsGainLoss,
      totalGainLossPercent: (realMetrics.totalInitial + virtualMetrics.totalInitial) > 0
        ? ((realMetrics.totalPositionsGainLoss + virtualMetrics.totalPositionsGainLoss) / 
           (realMetrics.totalInitial + virtualMetrics.totalInitial)) * 100
        : 0,
      totalPositions: realMetrics.totalPositions + virtualMetrics.totalPositions,
      totalCash: realTotalCash + virtualTotalCash,
      totalInvested: realMetrics.totalInvested + virtualMetrics.totalInvested,
      totalEarnings: realMetrics.totalEarnings + virtualMetrics.totalEarnings,
    };

    return {
      real: {
        ...realMetrics,
        totalCash: realTotalCash,
        totalGainLossPercent: realTotalGainLossPercent,
        totalGainLoss: realMetrics.totalPositionsGainLoss,
      },
      virtual: {
        ...virtualMetrics,
        totalCash: virtualTotalCash,
        totalGainLossPercent: virtualTotalGainLossPercent,
        totalGainLoss: virtualMetrics.totalPositionsGainLoss,
      },
      combined,
    };
  };

  const formatDateEST = (date: Date | undefined | null) => {
    if (!date) return '-';
    const estDate = toZonedTime(date, 'America/New_York');
    return format(estDate, 'MMM dd, yyyy h:mm a');
  };

  /**
   * Get the last activity date for a portfolio by looking at position entry/exit dates
   */
  const getLastActivityDate = (portfolioId: string): Date | null => {
    const openPos = positions[portfolioId] || [];
    const pendingPos = pendingPositions[portfolioId] || [];
    const closedPos = closedPositions[portfolioId] || [];

    let lastDate: Date | null = null;

    // Check all position entry dates and exit dates
    const allPositions = [...openPos, ...pendingPos, ...closedPos];

    for (const position of allPositions) {
      // Check entry date
      if (position.entryDate) {
        const entryDate = position.entryDate instanceof Date
          ? position.entryDate
          : new Date(position.entryDate);
        if (!lastDate || entryDate > lastDate) {
          lastDate = entryDate;
        }
      }

      // Check exit date (for closed positions)
      if (position.exitDate) {
        const exitDate = position.exitDate instanceof Date
          ? position.exitDate
          : new Date(position.exitDate);
        if (!lastDate || exitDate > lastDate) {
          lastDate = exitDate;
        }
      }
    }

    return lastDate;
  };

  const handleExecutePendingPosition = (position: Position) => {
    setExecutePendingDialog({ open: true, position });
  };

  const handlePendingPositionExecuted = () => {
    setExecutePendingDialog({ open: false, position: null });
    loadDashboardData();
  };

  const handleCancelPendingPosition = async (position: Position) => {
    if (window.confirm(`Are you sure you want to cancel this pending order for ${position.symbol}?`)) {
      try {
        await PositionService.cancelPendingPosition(position.id);
        loadDashboardData();
      } catch (error) {
        console.error('Error cancelling pending position:', error);
        alert('Failed to cancel pending position');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const overallMetrics = calculateOverallMetrics();

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 1.5 : 3 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'}>Dashboard</Typography>
        <Button
          variant="contained"
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          onClick={() => setCreateDialogOpen(true)}
          startIcon={<AccountBalance />}
        >
          {isMobile ? 'New' : 'Create Portfolio'}
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {portfolios.length > 0 && (
        <>
          {/* Overall Summary */}
          <Stack spacing={3}>
            {/* Real Portfolios Summary */}
            {overallMetrics.real.portfolioCount > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Real Portfolios Performance
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Value
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.real.totalValue)}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {overallMetrics.real.totalGainLoss >= 0 ? (
                          <TrendingUp color="success" fontSize="small" />
                        ) : (
                          <TrendingDown color="error" fontSize="small" />
                        )}
                        <Typography
                          variant="body2"
                          color={overallMetrics.real.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {overallMetrics.real.totalGainLoss >= 0 ? '+' : ''}
                          {formatCurrency(overallMetrics.real.totalGainLoss)} (
                          {formatPercent(overallMetrics.real.totalGainLossPercent)})
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Fund Size
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.real.totalInitial)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {overallMetrics.real.portfolioCount} portfolio{overallMetrics.real.portfolioCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Positions
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {overallMetrics.real.totalPositions}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Active positions
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Cash Available
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.real.totalCash)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Available to invest
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Earnings
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                        color={overallMetrics.real.totalEarnings >= 0 ? 'success.main' : 'error.main'}
                      >
                        {overallMetrics.real.totalEarnings >= 0 ? '+' : ''}
                        {formatCurrency(overallMetrics.real.totalEarnings)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Realized P&L
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Virtual Portfolios Summary */}
            {overallMetrics.virtual.portfolioCount > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Virtual Portfolios Performance
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Value
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.virtual.totalValue)}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {overallMetrics.virtual.totalGainLoss >= 0 ? (
                          <TrendingUp color="success" fontSize="small" />
                        ) : (
                          <TrendingDown color="error" fontSize="small" />
                        )}
                        <Typography
                          variant="body2"
                          color={overallMetrics.virtual.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {overallMetrics.virtual.totalGainLoss >= 0 ? '+' : ''}
                          {formatCurrency(overallMetrics.virtual.totalGainLoss)} (
                          {formatPercent(overallMetrics.virtual.totalGainLossPercent)})
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Fund Size
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.virtual.totalInitial)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {overallMetrics.virtual.portfolioCount} portfolio{overallMetrics.virtual.portfolioCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Positions
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {overallMetrics.virtual.totalPositions}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Active positions
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Cash Available
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {formatCurrency(overallMetrics.virtual.totalCash)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Available to test
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2.4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Earnings
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                        color={overallMetrics.virtual.totalEarnings >= 0 ? 'success.main' : 'error.main'}
                      >
                        {overallMetrics.virtual.totalEarnings >= 0 ? '+' : ''}
                        {formatCurrency(overallMetrics.virtual.totalEarnings)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Test P&L
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Stack>

          {/* Portfolio Cards */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Portfolios
          </Typography>
          <Grid container spacing={3}>
            {portfolios.map((portfolio) => {
              const portfolioPositions = positions[portfolio.id] || [];
              const portfolioPendingPositions = pendingPositions[portfolio.id] || [];
              const metrics = calculatePortfolioMetrics(portfolio, portfolioPositions, portfolioPendingPositions);

              return (
                <Grid item xs={12} sm={12} md={6} key={portfolio.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'box-shadow 0.3s',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => {
                      setSelectedPortfolio(portfolio);
                      setDetailsOpen(true);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {portfolio.name}
                          </Typography>
                          <Chip
                            label={portfolio.type}
                            color={portfolio.type === 'real' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </Box>
                        <Assessment color="action" />
                      </Box>

                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Total Value
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}
                          >
                            {formatCurrency(metrics.totalValue)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={metrics.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                          >
                            {metrics.totalGainLoss >= 0 ? '+' : ''}
                            {formatPercent(metrics.totalGainLossPercent)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Available
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}
                          >
                            {formatCurrency(metrics.availableFunds)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            {metrics.allocatedPercent.toFixed(1)}% allocated
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Pending
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}
                          >
                            {formatCurrency(metrics.pendingAllocations)}
                          </Typography>
                          <Typography variant="body2" color={metrics.totalFundUsedPercent > 100 ? 'error' : 'textSecondary'} sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            {metrics.totalFundUsedPercent.toFixed(1)}% used
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Total Earnings:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {(portfolio.totalEarnings || 0) >= 0 ? '+' : ''}
                          {formatCurrency(portfolio.totalEarnings || 0)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {metrics.positionCount} / {portfolio.maxPositions} positions
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Updated: {formatDateEST(getLastActivityDate(portfolio.id) || portfolio.updatedAt)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {portfolios.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <AccountBalance sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Welcome to Trade Advisor
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Create your first portfolio to start tracking your trades
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<AccountBalance />}
          >
            Create Portfolio
          </Button>
        </Box>
      )}

      <CreatePortfolioDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handlePortfolioCreated}
      />

      {/* Portfolio Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedPortfolio?.name} - Portfolio Details
          <Typography variant="body2" color="textSecondary">
            Created: {formatDateEST(selectedPortfolio?.createdAt)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedPortfolio && (
            <Box>
              <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fund Size
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedPortfolio.initialFundValue)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Available Funds
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      calculatePortfolioMetrics(
                        selectedPortfolio,
                        positions[selectedPortfolio.id] || [],
                        pendingPositions[selectedPortfolio.id] || []
                      ).availableFunds
                    )}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Value
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      calculatePortfolioMetrics(
                        selectedPortfolio,
                        positions[selectedPortfolio.id] || [],
                        pendingPositions[selectedPortfolio.id] || []
                      ).totalValue
                    )}
                  </Typography>
                </Paper>
              </Stack>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Open Positions
              </Typography>
              {positions[selectedPortfolio.id]?.filter(p => p.status === 'open').length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Entry Price</TableCell>
                        <TableCell align="right">Current Price</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="center">Gain/Loss</TableCell>
                        <TableCell>Entry Date</TableCell>
                        <TableCell align="center">Days Held</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions[selectedPortfolio.id]
                        .filter(p => p.status === 'open')
                        .map((position) => (
                          <TableRow key={position.id}>
                            <TableCell>{position.symbol}</TableCell>
                            <TableCell align="right">{position.quantity}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(position.entryPrice)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(position.currentPrice)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(position.currentPrice * position.quantity)}
                            </TableCell>
                            <TableCell align="center">
                              <Stack spacing={0.5} alignItems="center">
                                <Typography
                                  variant="body2"
                                  color={
                                    position.unrealizedGainLoss >= 0 ? 'success.main' : 'error.main'
                                  }
                                >
                                  {position.unrealizedGainLoss >= 0 ? '+' : ''}
                                  {formatCurrency(position.unrealizedGainLoss)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={
                                    position.unrealizedGainLoss >= 0 ? 'success.main' : 'error.main'
                                  }
                                >
                                  {position.unrealizedGainLoss >= 0 ? '+' : ''}
                                  {(
                                    ((position.currentPrice - position.entryPrice) /
                                      position.entryPrice) *
                                    100
                                  ).toFixed(2)}
                                  %
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={position.holdingDays}
                                color={position.holdingDays >= 28 ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No open positions in this portfolio
                </Typography>
              )}

              {/* Pending Positions Section */}
              {pendingPositions[selectedPortfolio.id]?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Pending Positions
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Limit Price</TableCell>
                          <TableCell align="center">Current/Limit %</TableCell>
                          <TableCell align="right">Allocated Funds</TableCell>
                          <TableCell>Order Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingPositions[selectedPortfolio.id].map((position) => {
                          const limitPrice = position.limitPrice || position.entryPrice;
                          const priceDiffPercent = position.currentPrice 
                            ? ((position.currentPrice - limitPrice) / limitPrice) * 100 
                            : 0;
                          
                          return (
                            <TableRow key={position.id}>
                              <TableCell>{position.symbol}</TableCell>
                              <TableCell align="right">{position.quantity}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(limitPrice)}
                              </TableCell>
                              <TableCell align="center">
                                {position.currentPrice ? (
                                  <Typography 
                                    variant="body2" 
                                    color={priceDiffPercent >= 0 ? 'error.main' : 'success.main'}
                                  >
                                    {priceDiffPercent >= 0 ? '+' : ''}{priceDiffPercent.toFixed(2)}%
                                  </Typography>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(limitPrice * position.quantity)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleExecutePendingPosition(position)}
                                  >
                                    Execute
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleCancelPendingPosition(position)}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Transaction History Section */}
              {closedPositions[selectedPortfolio.id]?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    Transaction History
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Entry Price</TableCell>
                          <TableCell>Entry Date</TableCell>
                          <TableCell align="right">Exit Price</TableCell>
                          <TableCell>Exit Date</TableCell>
                          <TableCell align="right">Days Held</TableCell>
                          <TableCell align="center">Realized P&L</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {closedPositions[selectedPortfolio.id].map((position) => {
                          const daysHeld = position.entryDate && position.exitDate
                            ? differenceInDays(startOfDay(position.exitDate), startOfDay(position.entryDate))
                            : 0;
                          
                          return (
                            <TableRow key={position.id}>
                              <TableCell>{position.symbol}</TableCell>
                              <TableCell align="right">{position.quantity}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(position.entryPrice)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.entryDate)}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(position.exitPrice || 0)}
                              </TableCell>
                              <TableCell>{formatDateEST(position.exitDate)}</TableCell>
                              <TableCell align="right">{daysHeld}</TableCell>
                              <TableCell align="center">
                                <Stack spacing={0.5} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    color={
                                      (position.realizedGainLoss || 0) >= 0 ? 'success.main' : 'error.main'
                                    }
                                    fontWeight="bold"
                                  >
                                    {(position.realizedGainLoss || 0) >= 0 ? '+' : ''}
                                    {formatCurrency(position.realizedGainLoss || 0)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      (position.realizedGainLoss || 0) >= 0 ? 'success.main' : 'error.main'
                                    }
                                  >
                                    {position.entryPrice > 0 ? (
                                      <>
                                        {(position.realizedGainLoss || 0) >= 0 ? '+' : ''}
                                        {(
                                          ((position.realizedGainLoss || 0) /
                                            (position.entryPrice * position.quantity)) *
                                          100
                                        ).toFixed(2)}
                                        %
                                      </>
                                    ) : (
                                      '-'
                                    )}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Execute Pending Position Dialog */}
      <ExecutePendingPositionDialog
        open={executePendingDialog.open}
        onClose={() => setExecutePendingDialog({ open: false, position: null })}
        position={executePendingDialog.position}
        onExecuted={handlePendingPositionExecuted}
      />

      {/* Welcome Dialog for new users */}
      <WelcomeDialog />
    </Box>
  );
};

export default Dashboard;